import type { ProductVariant } from '../types/pz';
import { getProductSelectionLabel } from './productSelectorLabel';

/**
 * Maps a product's `baseUnit` enum value (e.g. "SQUARE_METER") to the WZ line
 * unit code (e.g. "m2") shown in the WZ unit Select.
 *
 * Shared invariant with PRODUCT_UNIT_TO_SUPPLIER_UNIT (modules/orders/supplierOrderVariantUtils.ts)
 * and UNIT_BY_PRODUCT_UNIT (modules/products/orderVariantSelection.ts): every `baseUnit` enum value
 * must have an entry here, with `pcs` as the safe fallback. The OUTPUT vocabularies
 * differ on purpose — the WZ unit Select accepts kg/m/l, so this map keeps those
 * distinct, whereas the supplier/order-line maps collapse them to `pcs`. So when the
 * baseUnit enum changes, add the key to all three; do NOT copy the values across.
 */
export const PRODUCT_UNIT_TO_WZ_UNIT: Record<string, string> = {
  PIECE: 'pcs',
  KILOGRAM: 'kg',
  METER: 'm',
  LINEAR_METER: 'm',
  SQUARE_METER: 'm2',
  CUBIC_METER: 'm3',
  LITER: 'l',
  SET: 'pcs',
  PACKAGE: 'pcs',
};

/** Canonical WZ line unit codes — the values offered by the WZ unit Select. */
const WZ_UNIT_CODES = new Set(['pcs', 'kg', 'm', 'm2', 'm3', 'l']);

/**
 * Normalize any incoming unit to a canonical WZ line code so the WZ unit Select
 * always has a matching option and read-mode formatUnit shows a proper label.
 *
 * Order lines default their unit to the raw `ProductUnit` enum ('PIECE'), so a
 * WZ generated from such an order — or an older WZ saved with that value — would
 * otherwise carry 'PIECE' into the Select, which has no such option and renders
 * the raw enum on a Polish screen.
 *   - already a WZ code ('pcs', 'm2', …) → returned unchanged
 *   - ProductUnit enum ('PIECE', 'SQUARE_METER', …) → mapped via PRODUCT_UNIT_TO_WZ_UNIT
 *   - empty / unknown → 'pcs'
 */
export function toWzUnitCode(unit?: string | null): string {
  if (!unit) return 'pcs';
  if (WZ_UNIT_CODES.has(unit)) return unit;
  return PRODUCT_UNIT_TO_WZ_UNIT[unit] ?? 'pcs';
}

/** Existing WZ-line fields the autofill reads for its fallback chains. */
export interface WzVariantAutofillLine {
  productName?: string;
  productCode?: string;
  width?: number;
  height?: number;
  length?: number;
  thickness?: number;
  weight?: number;
  netWeight?: number;
  grossWeight?: number;
  productCategoryDimensionDisplayProfile?: string | null;
}

/** Fields the autofill writes onto a WZ line. Spread onto the existing item. */
export interface WzVariantAutofillPatch {
  productVariantId?: string;
  productName?: string;
  productCode?: string;
  unit?: string;
  width?: number;
  height?: number;
  length?: number;
  thickness?: number;
  weight?: number;
  netWeight?: number;
  grossWeight?: number;
  productCategoryDimensionDisplayProfile?: string | null;
}

/**
 * Single home for "apply a catalog variant selection onto a WZ line". Mirrors
 * getVariantOrderLinePatch (modules/products/orderVariantSelection.ts) /
 * buildSupplierOrderVariantAutofill (modules/orders/supplierOrderVariantUtils.ts)
 * for the WZ document surface. Used by both the WZ
 * create form (WZNew) and the WZ edit form (WZDetail) so the autofill rule cannot drift.
 *
 * Returns a patch to spread onto the current line. Clearing the picker (no variantId)
 * only unsets `productVariantId`, leaving the operator's other fields intact so the
 * free-text productName entry still works.
 */
export function buildWzVariantAutofill(
  line: WzVariantAutofillLine,
  variantId: string | undefined,
  variant?: ProductVariant
): WzVariantAutofillPatch {
  if (!variantId || !variant) {
    return { productVariantId: undefined };
  }
  const unit = PRODUCT_UNIT_TO_WZ_UNIT[variant.product?.baseUnit ?? ''] ?? 'pcs';
  const netWeight = variant.netWeight ?? variant.product?.netWeight;
  const grossWeight = variant.grossWeight ?? variant.product?.grossWeight;
  return {
    productVariantId: variantId,
    productName: getProductSelectionLabel(variant, line.productName || ''),
    productCode: variant.product?.code || line.productCode || '',
    unit,
    width: variant.product?.width ?? variant.width ?? line.width,
    height: variant.product?.height ?? variant.height ?? line.height,
    length: variant.product?.length ?? variant.length ?? line.length,
    thickness: variant.product?.thickness ?? variant.thickness ?? line.thickness,
    weight: grossWeight ?? netWeight ?? line.weight,
    netWeight: netWeight ?? line.netWeight,
    grossWeight: grossWeight ?? line.grossWeight,
    productCategoryDimensionDisplayProfile:
      variant.product?.productCategoryDimensionDisplayProfile
      ?? line.productCategoryDimensionDisplayProfile
      ?? null,
  };
}
